import ATime from './ATime';
import MLeg from './MLeg';
import Helper from './Helper';

// TripResultStructure: https://github.com/VDVde/TRIAS/blob/1aed3e8a8f63a081ed15e026ab4e25e49b0f2302/Trias_Trips.xsd#L269
// TripLegStructure: https://github.com/VDVde/TRIAS/blob/1aed3e8a8f63a081ed15e026ab4e25e49b0f2302/Trias_Trips.xsd#L337
// TimedLegStructure: https://github.com/VDVde/TRIAS/blob/1aed3e8a8f63a081ed15e026ab4e25e49b0f2302/Trias_Trips.xsd#L357
class MTrip {
  constructor(
    tripResult,
    index,
  ) {
    // elements
    this.element = document.createElement('li');
    this.element.classList.add('m-trip');

    this.summaryElement = document.createElement('div');
    this.summaryElement.classList.add('m-trip__summary');

    this.dateElement = document.createElement('div');
    this.dateElement.classList.add('m-trip__date');

    this.timeElement = document.createElement('div');
    this.timeElement.classList.add('m-trip__time');

    this.durationElement = document.createElement('time');
    this.durationElement.classList.add('m-trip__duration');

    this.priceElement = document.createElement('a');
    this.priceElement.setAttribute('href', '/fahrkarten#wo-kaufen');
    this.priceElement.setAttribute('title', 'Wo kaufe ich meine Fahrkarte?');
    this.priceElement.classList.add('m-trip__price');

    this.legsSummaryElement = document.createElement('div');
    this.legsSummaryElement.classList.add('m-trip__legs-summary');

    this.detailsElement = document.createElement('details');
    this.detailsElement.classList.add('m-trip__details');

    // variables
    this.index = index;
    this.id = tripResult['trias:ResultId'];
    this.trip = tripResult['trias:Trip'];
    this.tripFares = tripResult['trias:TripFares'];
    // make sure this.legs is always an array.
    this.legs = Array.isArray(this.trip['trias:TripLeg'])
      ? this.trip['trias:TripLeg']
      : [this.trip['trias:TripLeg']];
    this.currentStyle = null;
    this.data = tripResult;
    this.mLegs = this.legs.map((tripLeg) => new MLeg(tripLeg, this.legs.length, this.trip));

    // methods
    // Blob instead of a data: URL: since the WebKit fix for CVE-2026-43701 Safari
    // refuses to download data: URLs ("not initiated by API client"), which broke
    // the calendar button on iOS 26/27.
    // see https://developer.mozilla.org/en-US/docs/Web/API/URL/createObjectURL_static
    this.stringToObjectUrl = (string, mediatype = 'text/plain') => {
      const blob = new Blob([string], { type: `${mediatype};charset=utf-8` });
      return URL.createObjectURL(blob);
    };
    this.replaceLineBreaks = (string) => string.replace(/\n/g, '\\n');
    // trim spaces but not new lines
    this.trimSpaces = (string) => string.replace(/^\u0020+/gm, '');
    this.dateToLocaleDateString = (date) => (new Date(date)).toLocaleString('de-DE', {
      dateStyle: 'long',
    });

    // event listener
    this.element.addEventListener('click', () => {
      this.setCurrent();
    }, {
      capture: true,
    });

    // init
    this.element.setAttribute('id', this.id);
    this.updateElement();
    this.summaryElement.appendChild(this.dateElement);
    this.summaryElement.appendChild(this.timeElement);
    this.summaryElement.appendChild(this.durationElement);
    this.summaryElement.appendChild(this.priceElement);
    this.summaryElement.appendChild(this.legsSummaryElement);
    this.element.appendChild(this.summaryElement);
    this.element.appendChild(this.detailsElement);
  }

  get duration() {
    return this.trip?.['trias:Duration'];
  }

  get startName() {
    return this.mLegs.at(0).startName;
  }

  get endName() {
    return this.mLegs.at(-1).endName;
  }

  // estimated start time
  get startTime() {
    return this.trip?.['trias:StartTime'];
  }

  // planned start time
  get startTimeTimetabled() {
    return this.mLegs.at(0).startTime;
  }

  get endTime() {
    return this.trip?.['trias:EndTime'];
  }

  // planned start time
  get endTimeTimetabled() {
    return this.mLegs.at(-1).endTime;
  }

  get buttonsElement() {
    // has to be span to differentiate to .m-leg
    const buttonsElement = document.createElement('span');
    buttonsElement.classList.add('m-trip__buttons');

    const buttonPrintElement = Object.assign(document.createElement('button'), {
      className: 'a-button -small -outline',
      innerText: 'Drucken',
    });

    this.icsObjectUrl = this.stringToObjectUrl(this.trimSpaces(this.ics), 'text/calendar');
    const buttonSaveElement = Object.assign(document.createElement('a'), {
      className: 'a-button -small -outline',
      innerText: 'In Kalender speichern',
      href: this.icsObjectUrl,
      download: `Verbindung ${this.startName} – ${this.endName}.ics`,
    });

    buttonPrintElement.addEventListener('click', async () => {
      await window.oTripRequest.prepareForPrint();
      window.print();
      /* eslint-disable-next-line no-underscore-dangle */
      window._paq?.push(['trackEvent', 'Verbindungsauskunft', 'Click', 'Print', this.index + 1]);
    });
    buttonPrintElement.addEventListener('click', () => {
      /* eslint-disable-next-line no-underscore-dangle */
      window._paq?.push(['trackEvent', 'Verbindungsauskunft', 'Click', 'Save to Calendar', this.index + 1]);
    });

    buttonsElement.appendChild(buttonPrintElement);
    buttonsElement.appendChild(buttonSaveElement);

    return buttonsElement;
  }

  get isCurrent() {
    return this.element.getAttribute('aria-current') === 'true';
  }

  get icsDescription() {
    const {
      mLegs, startTime, startName, endName,
    } = this;

    const legs = mLegs.map((mLeg) => mLeg.icsText);
    let string = `Verbindung ${startName} nach ${endName}
      Datum: ${this.dateToLocaleDateString(startTime)}
      Dauer: ${Helper.convertISO8601ToString(this.duration).replace('&thinsp;', ' ')}

      -
      ${legs.join('\n-\n')}
      -

      Echtzeitinformationen sind nicht berücksichtigt. Alle Angaben ohne Gewähr. Fahrplanänderungen vorbehalten.`;
    string = this.trimSpaces(string);
    return this.replaceLineBreaks(string);
  }

  get ics() {
    const {
      startTimeTimetabled, endTimeTimetabled, id, startName, endName,
    } = this;
    const now = (new Date()).toJSON().replace(/[-:]/g, '').replace(/\.\d{3}/, '');

    return `
      BEGIN:VCALENDAR
      VERSION:2.0
      X-LOTUS-CHARSET:UTF-8
      PRODID:https://www.reutlinger-stadtverkehr.de
      METHOD:PUBLISH
      BEGIN:VTIMEZONE
      TZID:Europe/Berlin
      X-LIC-LOCATION:Europe/Berlin
      BEGIN:DAYLIGHT
      TZOFFSETFROM:+0100
      TZOFFSETTO:+0200
      TZNAME:CEST
      DTSTART:19700329T020000
      RRULE:FREQ=YEARLY;INTERVAL=1;BYDAY=-1SU;BYMONTH=3
      END:DAYLIGHT
      BEGIN:STANDARD
      TZOFFSETFROM:+0200
      TZOFFSETTO:+0100
      TZNAME:CET
      DTSTART:19701025T030000
      RRULE:FREQ=YEARLY;INTERVAL=1;BYDAY=-1SU;BYMONTH=10
      END:STANDARD
      END:VTIMEZONE
      BEGIN:VEVENT
      UID:${id}@reutlinger-stadtverkehr.de
      LOCATION:${this.startName}
      CLASS:PUBLIC
      SUMMARY:${startName} → ${endName}
      DTSTART:${startTimeTimetabled.replace(/[-:]/g, '')}
      DTEND:${endTimeTimetabled.replace(/[-:]/g, '')}
      DTSTAMP:${now}
      DESCRIPTION:${this.icsDescription}
      URL;VALUE=URI:${window.location.href}
      END:VEVENT
      END:VCALENDAR
    `;
  }

  setCurrent() {
    if (this.element.getAttribute('aria-current') !== 'true') {
      window.oTripRequest.mTrips.forEach((mTrip) => {
        if (mTrip !== this) {
          mTrip.removeCurrent();
        }
      });
      this.element.setAttribute('aria-current', 'true');

      /* eslint-disable-next-line no-underscore-dangle */
      window._paq?.push(['trackEvent', 'Verbindungsauskunft', 'Click', `Verbindung ${this.index + 1}, ${this.duration}`, this.index + 1]);
    }
    this.showRoute();
  }

  // revokes the object URL behind the calendar button
  destroy() {
    if (this.icsObjectUrl) {
      URL.revokeObjectURL(this.icsObjectUrl);
      this.icsObjectUrl = null;
    }
  }

  removeCurrent() {
    this.element.removeAttribute('aria-current');
    this.removeRoute();
  }

  expand() {
    this.detailsElement.open = true;
    this.mLegs.forEach((mLeg) => {
      mLeg.expand();
    });
  }

  removeRoute() {
    this.mLegs.forEach((mLeg) => {
      mLeg.removeRoute();
    });
  }

  showRoute(instant = false) {
    let allCoordinates = [];

    // remove routes from other trips
    window.oTripRequest.mTrips.forEach((mTrip) => {
      if (mTrip !== this) {
        mTrip.removeRoute();
      }
    });

    this.mLegs.forEach((mLeg) => {
      mLeg.showRoute();
      allCoordinates = [...allCoordinates, ...mLeg.coordinates];
    });

    window.mMap?.fitCoordinates(allCoordinates, instant ? 0 : null);
  }

  updateDate() {
    const { startTime } = this;

    const dateToLocaleDateString = (date) => (new Date(date)).toLocaleString('de-DE', {
      dateStyle: 'full',
    });

    this.dateElement.innerHTML = `
      <span class="m-trip__date">${dateToLocaleDateString(startTime)}</span>
    `;
  }

  updateTime() {
    const { startTime, endTime } = this;

    let departureTimetabled = null;
    const firstLeg = this.legs.at(0);
    const firstLegDeparture = firstLeg?.['trias:TimedLeg']?.['trias:LegBoard']?.['trias:ServiceDeparture'];
    if (firstLegDeparture) {
      departureTimetabled = firstLegDeparture?.['trias:TimetabledTime'] ?? null;
    }
    let arrivalTimetabled = null;
    const lastLeg = this.legs.at(-1);
    const lastLegArrival = lastLeg?.['trias:TimedLeg']?.['trias:LegAlight']?.['trias:ServiceArrival'];
    if (lastLegArrival) {
      arrivalTimetabled = lastLegArrival?.['trias:TimetabledTime'] ?? null;
    }

    this.timeElement.innerHTML = `
      <span class="a-visually-hidden">Abfahrt: </span>
      ${(new ATime(startTime, departureTimetabled)).element.outerHTML}
      –
      <span class="a-visually-hidden">Ankunft: </span>
      ${(new ATime(endTime, arrivalTimetabled)).element.outerHTML}
    `;
  }

  get einzelfahrscheinErwachsenerTicket() {
    return this.tripFares?.['trias:Ticket']?.find((ticket) => ticket['trias:TicketId'] === '1011') ?? null;
  }

  updatePrice() {
    const price = this.einzelfahrscheinErwachsenerTicket?.['trias:Price'] ?? null;
    if (price) {
      this.priceElement.innerHTML = Helper.formatPrice(price);
    } else {
      this.priceElement.hidden = true;
    }
  }

  updateDuration() {
    this.durationElement.setAttribute('datetime', this.duration);
    this.durationElement.innerHTML = Helper.convertISO8601ToString(this.duration);
  }

  updateLegsSummary() {
    this.mLegs.forEach((mLeg) => {
      this.legsSummaryElement.appendChild(mLeg.legsSummaryElement);
    });
  }

  updateDetails() {
    const summaryElement = document.createElement('summary');
    summaryElement.innerText = 'Details';
    this.detailsElement.appendChild(summaryElement);

    const containerElement = document.createElement('div');
    containerElement.classList.add('m-trip__details-container');
    this.detailsElement.appendChild(containerElement);

    this.mLegs.forEach((mLeg) => {
      containerElement.appendChild(mLeg.element);
    });

    containerElement.appendChild(this.buttonsElement);

    this.detailsElement.addEventListener('toggle', () => {
      if (this.detailsElement.open) {
        /* eslint-disable-next-line no-underscore-dangle */
        window._paq?.push(['trackEvent', 'Verbindungsauskunft', 'Details', 'Verbindung', this.index + 1]);
      }
    });
  }

  updateElement() {
    this.updateDate();
    this.updateTime();
    this.updateDuration();
    this.updatePrice();
    this.updateLegsSummary();
    this.updateDetails();
  }
}

export default MTrip;
